
import { AfterContentInit, Input, Output, EventEmitter, Directive } from '@angular/core';
import { PlycaDataService } from '../../../services/plyca.service';
import { ChildOfProjectDTO, TaskDTO } from '../../api/plyca-dtos';
import { ProjectEntityDTO, ValidationResultDTO, ResultTypeDTO, getResultTypeDTOLevel } from '../../api/plyca-dtos-v2';
import {
  FormConfig,
  FormAction,
  FormDialogResponse,
  CancelFormAction
} from '../../components/forms/form.objects';
import { formPlycaComponent } from '../../components/forms/formPlyca.component';
import { Observable, of, throwError } from 'rxjs';
import * as lodash from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { UIService } from '../../ui.service';
import { ServiceLocator } from '../../ServiceLocator';
import { WaitLoadingService } from '../../components/common/waitLoading/wait-loading.service';
import { catchError, finalize, mergeMap, switchMap, take, tap } from 'rxjs/operators';
import { HelpService } from '../../components/common/help/help.service';
import { AppConfiguration } from '../../conf/app.configuration';
import { PlycaErrorHandler } from '../../../http/PlycaErrorHandler';
import { ConfirmationService } from 'primeng-lts/api';
import { DialogService, DynamicDialogConfig } from 'primeng-lts/dynamicdialog';
import { BaseComponent } from '../../components/common/baseComponent/base.component';
import { UserService } from '../../user.service';
import { FormService } from '../../components/forms/form.service';

const CANCEL_ACTION = new CancelFormAction();

@Directive()
export abstract class TenderingBaseComponent<T extends ChildOfProjectDTO<any>> extends BaseComponent
  implements AfterContentInit {
  @Input()
  project: ProjectEntityDTO;
  @Input()
  data: Array<T>;
  @Input()
  task: TaskDTO;
  @Input()
  validations: Map<any, ValidationResultDTO>;
  @Output()
  updated = new EventEmitter<any>();
  @Output()
  editDialogOpened = new EventEmitter<boolean>();

  colHeaders: string[];
  loaded = false;

  protected plycaService: PlycaDataService;
  public translationService: TranslateService;
  protected dialogService: DialogService;
  protected uiService: UIService;
  private confirmationService: ConfirmationService;
  protected waitService: WaitLoadingService;
  public helpService: HelpService;
  private plycaErrorHandler: PlycaErrorHandler;
  private formService: FormService;

  private isModel = false;

  // datos modal validacion
  public showValidationInfo = false;
  public validationResult: ValidationResultDTO;

  public LOADINGRESULT_TYPE = ResultTypeDTO.LOADING;
  appConfig: AppConfiguration;


  constructor() {
    super();
    this.plycaService = ServiceLocator.injector.get(PlycaDataService);
    this.translationService = ServiceLocator.injector.get(TranslateService);
    this.dialogService = ServiceLocator.injector.get(DialogService);
    this.uiService = ServiceLocator.injector.get(UIService);
    this.confirmationService = ServiceLocator.injector.get(ConfirmationService);
    this.waitService = ServiceLocator.injector.get(WaitLoadingService);
    this.helpService = ServiceLocator.injector.get(HelpService);
    this.plycaErrorHandler = ServiceLocator.injector.get(PlycaErrorHandler);
    this.appConfig = ServiceLocator.injector.get(AppConfiguration);
    this.formService = ServiceLocator.injector.get(FormService);
  }

  public getDossierId(): string {
    return this.project.identifier;
  }

  ngAfterContentInit(): void {
    this.loaded = true;
    if (this.project.documentClass) {
      this.isModel = this.project.documentClass.identifier === this.appConfig.getValue('dossierModel.documentClass');
    }
  }

  abstract getServiceURL(): string;
  abstract getFormSettingURL(): string;
  abstract getTagsIdentifier(): string[];
  abstract getDialogTitle(): string;
  abstract dtoToBean(dto: T): any;
  abstract beanToDTO(bean: any): T;

  /**
   * Define el contexto de validacion a aplicar al recurso de tipo T
   */
  getValidationCtx(): string {
    return (this.isModel) ? '' : 'FLIC';
  }

  getDialogConfig(titleKey?: string): DynamicDialogConfig {
    return {
      header: this.translationService.instant(
        titleKey ? titleKey : this.getDialogTitle()
      ),
      width: '70%',
      style: { overflow: 'hidden' },
      styleClass: 'formEdition',
      closable: true,
      closeOnEscape: false
    } as DynamicDialogConfig;
  }

  public isLoaded(): boolean {
    return this.loaded;
  }

  public setLoaded(value: boolean) {
    this.loaded = value;
  }

  public newData(): void {
    this.editData();
  }



  public editData(rowData?: T, tags?: string[]
  ): void {
    let config = this.getDialogConfig();
    let data: T;
    if (!rowData) {
      data = <T>{};
    } else {
      data = lodash.cloneDeep(rowData);
    }
    if (!data.project) {
      data.project = this.project;
    }
    let actions = new Array<FormAction<any>>();
    actions.push(
      this.getActionForm()
    );
    actions.push(new CancelFormAction());


    config['formConfig'] = new FormConfig(undefined,
      this.getFormSettingURL(),
      this.dtoToBean(data),
      actions,
      true,
      true,
      tags != null ? tags : this.getTagsIdentifier()
    );
    this.editDialogOpened.emit(true);
    const dialogRef = this.dialogService.open(formPlycaComponent, config);
    dialogRef.onClose.pipe(take(1)).subscribe(
      (response: FormDialogResponse<T>) => {
        if (response && response.actionId !== 'cancel') {
          this.emitUpdate(response.model.identifier, response.model);
        }
        this.editDialogOpened.emit(false);
      },
      (error: any) => {
        this.showServerError(error);
        this.editDialogOpened.emit(false);
      }
    );
  }
  protected getActionForm(): FormAction<any> {
    return new TenderingBaseAction<T>(this.plycaService,
      this.confirmationService,
      this.translationService,
      this.waitService,
      this.formService,
      this.getServiceURL(),
      this.getValidationCtx(),
      this.beanToDTO,
      this.task,
      'General.save', 'btn btn-primary', 'fa fa-save', 'aceptar');
  }

  protected emitUpdate(identifier: any, updatedData?: any): void {
    this.waitService.startWaiting();
    this.updated.emit(identifier);
  }

  protected showServerError(error: any): void {
    this.plycaErrorHandler.showServerError(error);
  }

  deleteData(rowData: T): void {
    this.confirmationService.confirm({
      message: this.translationService.instant('General.confirm.action.delete'),
      accept: () => {
        this.waitService.startWaiting();
        this.subscribe(this.plycaService
          .delete<T>(
            this.getServiceURL(),
            rowData.identifier,
            'Delete' + this.getValidationCtx()
          )
          .pipe(
            finalize(() => this.waitService.cancelWaiting())
          ),
          () => this.objectDeleted(rowData.identifier),
          (error: any) => this.showServerError(error)
        );
      }
    });
  }

  protected objectDeleted(identifier: any): void {
    this.updated.emit(undefined);
  }

  public showValidationResult(validation: ValidationResultDTO): void {
    this.showValidationInfo = true;
    this.validationResult = validation;
  }

  public showValidationResultById(identifier: any): void {
    this.showValidationResult(this.validations.get(identifier));
  }

  public closeValidationResult(): void {
    this.showValidationInfo = false;
    this.validationResult = undefined;
  }

}

export class TenderingBaseAction<T extends ChildOfProjectDTO<any>>
  implements FormAction<any> {
  buttonTextKey: string;
  buttonClass: string;
  imageClass: string;
  closeOnSuccess = true;
  visibility = true;
  actionId: string;
  private userService: UserService;

  constructor(
    private plycaService: PlycaDataService,
    private confirmationService: ConfirmationService,
    private translationService: TranslateService,
    protected waitService: WaitLoadingService,
    private formService: FormService,
    private url: string,
    private validationCtx: string,
    private transformationFunc: Function,
    private task: TaskDTO,
    buttonTextKey: string,
    buttonClass: string,
    imageClass: string,
    actionId: string

  ) {
    this.buttonTextKey = buttonTextKey;
    this.buttonClass = buttonClass;
    this.imageClass = imageClass;
    this.actionId = actionId;
    this.userService = ServiceLocator.injector.get(UserService);
    this.visibility = true;
  }

  public executeFormAction(model: any): Observable<any> {
    this.waitService.startWaiting();
    return this.insertOrUpdate(model).pipe(
      finalize(() => this.waitService.cancelWaiting())
    );
  }

  private insertOrUpdate(model: any, errorLevel = ResultTypeDTO.OK): Observable<any> {
    let modelCopy = lodash.cloneDeep(model);
    let objToInsertOrUpdate = this.transformationFunc(modelCopy);
    // definicion operacion de insercion/actualizacion
    let insUpdtObs = this.insertOrUpdateObsFunction(modelCopy, objToInsertOrUpdate, errorLevel);
    // validar
    return insUpdtObs.pipe(
      catchError((error: any) => {
        // si es un fatal vendra un httpError en otro caso en la propiedad validationResult
        const validationResult = error?.validationResult || error?.error || error;
        if (this.formService.isValidationResponse(validationResult)) {
          if (getResultTypeDTOLevel(validationResult.result) < getResultTypeDTOLevel(ResultTypeDTO.FATAL)) {
            this.waitService.cancelWaiting();
            const msg = this.translationService.instant('TenderDefinition.validationWarnMessage');
            const confService = this.confirmationService;
            return Observable.create(function (observer) {
              confService.confirm({
                message: msg,
                accept: () => {
                  // emitir true para grabar
                  observer.next(true);
                  observer.complete();
                },
                reject: () => {
                  // emitir false para no grabar
                  observer.next(false);
                  observer.complete();
                }
              });
            }).pipe(
              mergeMap((save: boolean) => {
                this.closeOnSuccess = save;
                if (save) {
                  return this.insertOrUpdate(model, ResultTypeDTO.FATAL);
                } else {
                  return throwError(error);
                }
              })
            );
          }
        }
        // mostrar errores y evitar cierre ventana
        this.closeOnSuccess = false;
        return throwError(validationResult);
      }),
      tap((object: any) => {
        this.closeOnSuccess = true;
      })
    );
  }
  private insertOrUpdateObsFunction(modelCopy: any, objToInsertOrUpdate: any, errorLevel: ResultTypeDTO): Observable<T> {
    let insUpdtObs: Observable<T>;
    if (modelCopy.identifier) {
      insUpdtObs = this.plycaService.updateObject<T>(
        this.url,
        objToInsertOrUpdate,
        this.getValidationCtxName(modelCopy),
        errorLevel
      );
    } else {
      insUpdtObs = this.plycaService.insert<T>(
        this.url,
        objToInsertOrUpdate,
        this.getValidationCtxName(modelCopy),
        errorLevel
      );
    }
    return insUpdtObs;
  }

  private getValidationCtxName(model: any): string {
    return (model.identifier ? 'Update' : 'Insert') + this.validationCtx;
  }
}
